import { enSerie } from "../core/enSerie.js";
import { crearAsiento } from "../core/accounting.js";
import { sellarAutor } from "../core/autoria.js";
import { getParametrosFiscales, saveData, siguienteNumeroDoc } from "../core/cloudSync.js";
import { C, EMPRESA, REGIMENES_FISCALES } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, genId, inputStyle, normalize, parseNumber, today } from "../core/utils.js";
import { render } from "../ui/app.js";
import { exportCuentaCobroPDF } from "../export/pdf.js";

// ─── CxP directas y cuentas de cobro ─────────────────────────────────────────
// Un costo asociado a una OP (transporte, flete, un servicio de un tercero) no
// siempre amerita una orden de compra: muchas veces es una obligación directa
// con una persona, que va derecho a Cuentas por Pagar SIN perder su vínculo con
// la OP — porque golpea el margen final de esa orden. Después, esas CxP se
// legalizan agrupándolas en una CUENTA DE COBRO que la persona firma y cobra.
//
// Contabilidad:
//   · Al crear la CxP directa (causación del costo):
//       Db 6140  Costo asociado a órdenes   → afecta la utilidad bruta / margen
//       Cr 2365  ReteFuente por pagar       → lo retenido al tercero
//       Cr 2368  ReteICA por pagar          → si el municipio lo obliga
//       Cr 2315  Costos y gastos por pagar  → el NETO que se le queda debiendo
//   · Al pagar la cuenta de cobro:
//       Db 2315 · Cr 1110/1105 por el neto
//   La cuenta de cobro en sí no genera asiento: es el documento de legalización.
//
// LA RETENCIÓN SE PRACTICA AL CAUSAR, no al pagar: el art. 392 E.T. la ordena en
// el pago o abono en cuenta, "el que ocurra primero", y la causación ES el abono
// en cuenta. Retenerla en el pago la dejaría fuera del mes que le corresponde.
//
// Las tarifas salen de la FICHA del tercero (% retefuente, % ReteICA, régimen,
// autorretenedor), por la misma puerta que las facturas de proveedor
// (calcularImpuestosCausacion). Por eso una CxP directa exige ficha: sin ella no
// hay tarifa que aplicar, ni identificación con la que reportar la exógena.
import { calcularImpuestosCausacion } from "../core/impuestos.js";

const CTA_COSTO = "6140";
const CTA_XPAGAR = "2315";
const CTA_RETEFUENTE = "2365";
const CTA_RETEICA = "2368";

// Un costo asociado que se paga por cuenta de cobro es SIEMPRE un servicio de un
// tercero (transporte, marcación, mano de obra), y a los servicios la ley les
// fija una cuantía mínima de 4 UVT — no las 27 UVT de las compras generales.
function baseMinimaServiciosUVT() {
  const pf = getParametrosFiscales();
  return parseNumber(pf.retefuente_servicios_base_uvt) || 4;
}

// La ficha del tercero de un costo. El nombre del concepto es texto libre con
// datalist, así que se resuelve igual que en el resto del ERP: exacto primero,
// normalizado después.
export function terceroDeCosto(nombre) {
  const n = (nombre || "").trim();
  if (!n) return null;
  const provs = state.data.proveedores || [];
  return provs.find(p => (p.nombre || "").trim() === n)
      || provs.find(p => normalize(p.nombre || "") === normalize(n))
      || null;
}

// Las retenciones de una CxP directa, con el motivo cuando no aplican: un cero
// sin explicación es indistinguible de un olvido.
export function retencionesCxpDirecta(tercero, base) {
  const calc = calcularImpuestosCausacion(tercero || {}, base, null,
    { baseMinimaUVT: baseMinimaServiciosUVT() });
  return {
    rfPct: calc.rfPct, montoRF: calc.montoRF, motivoRF: calc.motivoRFNoAplica,
    ricaPct: calc.ricaPct, montoRICA: calc.montoRICA, motivoRICA: calc.motivoRICANoAplica,
    total: calc.montoRF + calc.montoRICA,
    neto: base - (calc.montoRF + calc.montoRICA)
  };
}

// ─── Tarifas sugeridas para un tercero que presta servicios ────────────────
// Solo SUGERENCIAS: lo que manda es lo que quede en la ficha, que es donde la
// contadora decide. Se ofrecen con su cita para no tener que buscarla, y la
// cuantía mínima la aplica calcularImpuestosCausacion con los Parámetros
// Fiscales vigentes.
export const RETENCIONES_SERVICIO = [
  { id: "transporte_carga",        pct: 1,  label: "Transporte de carga",                         cita: "Art. 392 E.T. — 1%" },
  { id: "transporte_pasajeros",    pct: 3.5,label: "Transporte terrestre de pasajeros",           cita: "Art. 392 E.T. — 3,5%" },
  { id: "servicios_declarante",    pct: 4,  label: "Servicios generales — declarante de renta",   cita: "Art. 392 E.T. — 4%" },
  { id: "servicios_no_declarante", pct: 6,  label: "Servicios generales — NO declarante",         cita: "Art. 392 E.T. — 6%" },
  { id: "honorarios_declarante",   pct: 11, label: "Honorarios y comisiones — declarante",        cita: "Art. 392 E.T. — 11%, sin cuantía mínima" },
  { id: "honorarios_no_declarante",pct: 10, label: "Honorarios y comisiones — NO declarante",     cita: "Art. 392 E.T. — 10%, sin cuantía mínima" },
  { id: "art383",                  pct: 0,  label: "Rentas de trabajo — tabla del Art. 383",      cita: "Art. 383 E.T. — 0% hasta 95 UVT de base gravable", declara383: true },
  { id: "ninguna",                 pct: 0,  label: "No practicar retención",                      cita: "" }
];

// Qué tarifa proponer según el concepto del costo: el transporte es el caso
// diario de Mathiz y es el que más se equivoca si se deja en la general.
export function retencionSugerida(concepto) {
  const c = normalize(concepto || "");
  if (/transport|flete|acarreo|carga|mensajer|domicilio|log[ií]stic/.test(c)) return "transporte_carga";
  if (/honorari|asesor|consultor|dise[nñ]|profesional/.test(c)) return "honorarios_no_declarante";
  return "servicios_no_declarante";
}

export function topeCuentaCobro() {
  return parseNumber(getParametrosFiscales().cuenta_cobro_tope) || 1700000;
}

// ─── Memoria de conceptos: ¿este concepto va a OCP o a CxP directa? ─────────
// El usuario decide la primera vez; el sistema recuerda y a la siguiente solo
// sugiere y pide el visto bueno. La memoria vive en la nube (tenant_config)
// como cualquier configuración.
export const COSTOS_VIA_KEY = "erp:costos_via";

function leerMemoriaVias() {
  try {
    const raw = localStorage.getItem(COSTOS_VIA_KEY);
    const m = raw ? JSON.parse(raw) : {};
    return m && typeof m === "object" ? m : {};
  } catch { return {}; }
}

export function aprenderVia(concepto, via) {
  const clave = normalize(concepto || "").trim();
  if (!clave || !via) return;
  const m = leerMemoriaVias();
  if (m[clave] === via) return;
  m[clave] = via;
  try { localStorage.setItem(COSTOS_VIA_KEY, JSON.stringify(m)); } catch { /* sin caché local */ }
}

// Sugerencia: primero lo aprendido; si el concepto es nuevo, una heurística
// razonable (los servicios de movilización suelen ser CxP directa).
const PATRON_CXP = /transport|flete|env[ií]o|acarreo|domicilio|mensajer|log[ií]stic|taxi|carga|bodegaje|instalaci[oó]n|mano de obra/;
export function viaSugerida(concepto) {
  const clave = normalize(concepto || "").trim();
  const m = leerMemoriaVias();
  if (m[clave]) return { via: m[clave], aprendida: true };
  return { via: PATRON_CXP.test(clave) ? "cxp" : "ocp", aprendida: false };
}

// ─── Sincronizar conceptos con via "cxp" → CxP directas ─────────────────────
// Misma filosofía que la sincronización de OCPs de costos: crear, actualizar
// por diferencia (con asiento de ajuste) y eliminar con reversa lo que se
// quitó — pero NUNCA tocar una CxP que ya está en una cuenta de cobro o pagada.
export function sincronizarCxpDirectasOP(op) {
  // En la misma fila que la sincronización de OCP de esta orden: la segunda
  // ejecución espera a la primera y ya encuentra los conceptos marcados.
  return enSerie("costos:" + op.id, () => _sincronizarCxpDirectasOP(op));
}

async function _sincronizarCxpDirectasOP(op) {
  const conceptos = (op._conceptosCosto || []).filter(c => !c._esComision);
  let lista = [...(state.data.cuentas_pagar || [])];
  const usadas = new Set();
  const bloqueadas = [];

  for (const c of conceptos) {
    if (c._via !== "cxp") continue;
    const total = Math.round((parseNumber(c.cantidad) || 0) * (parseNumber(c.valor_tercero) || 0));
    const nombre = (c.proveedor_nombre || "").trim();
    if (!nombre || total <= 0) continue;

    if (!c._ccid) c._ccid = genId();
    let cxp = c._cxpId ? lista.find(x => x.id === c._cxpId) : null;
    // Si el concepto perdió su vínculo (una copia vieja de la orden desde otro
    // equipo, por ejemplo), la CxP pendiente que nació de ESTE costo se
    // reconoce por el ccid que lleva en su origen y se reutiliza: una cuenta
    // por pagar por costo, nunca dos.
    if (!cxp) {
      cxp = lista.find(x => x.estado === "pendiente" && x._origen && x._origen.id === op.id
        && x._origen.ccid === c._ccid && !usadas.has(x.id)) || null;
      if (cxp) c._cxpId = cxp.id;
    }
    if (cxp && cxp.estado !== "pendiente") {
      // Ya legalizada o pagada: el concepto no puede moverla.
      usadas.add(cxp.id);
      if (Math.abs(total - (parseNumber(cxp.valor) || 0)) >= 1) bloqueadas.push(cxp.numero);
      continue;
    }

    if (!cxp) {
      // La ficha manda: de ella salen las tarifas de retención y la
      // identificación con la que se reporta la exógena. Sin ficha se crea la
      // CxP igual —no se pierde la obligación— pero queda marcada para que el
      // pop-up de datos faltantes la complete antes de legalizarla.
      const tercero = terceroDeCosto(nombre);
      const ret = retencionesCxpDirecta(tercero, total);
      cxp = {
        id: genId(), numero: await siguienteNumeroDoc("CXP"), fecha: op.fecha || today(),
        tercero_id: tercero ? tercero.id : null,
        tercero_nombre: tercero ? tercero.nombre : nombre,
        tercero_cedula: tercero ? (tercero.nit || "") : "",
        concepto: c.concepto || "Costo asociado", valor: total,
        retefuente: ret.montoRF, retefuente_pct: ret.rfPct,
        reteica: ret.montoRICA, reteica_pct: ret.ricaPct,
        neto: ret.neto,
        _origen: { tipo: "OP", id: op.id, numero: op.numero, ccid: c._ccid }, estado: "pendiente"
      };
      sellarAutor(cxp);
      try {
        const movs = [{ cuenta: CTA_COSTO, debito: total, tercero_id: cxp.tercero_id }];
        if (ret.montoRF > 0) movs.push({ cuenta: CTA_RETEFUENTE, credito: ret.montoRF, tercero_id: cxp.tercero_id });
        if (ret.montoRICA > 0) movs.push({ cuenta: CTA_RETEICA, credito: ret.montoRICA, tercero_id: cxp.tercero_id });
        movs.push({ cuenta: CTA_XPAGAR, credito: ret.neto, tercero_id: cxp.tercero_id });
        const ast = await crearAsiento(
          `Costo asociado ${cxp.numero} — ${cxp.concepto} — ${op.numero}`,
          { tipo: "OP", id: op.id, numero: op.numero }, movs, cxp.fecha);
        cxp._asientoId = ast.id;
      } catch (e) {
        alert(`⚠️ No se pudo causar el costo "${cxp.concepto}": ` + e.message);
        continue;
      }
      lista = [...lista, cxp];
      c._cxpId = cxp.id;
    } else {
      const delta = total - (parseNumber(cxp.valor) || 0);
      const tercero = terceroDeCosto(nombre);
      const ret = retencionesCxpDirecta(tercero, total);
      if (Math.abs(delta) >= 1) {
        // El ajuste mueve el costo Y las retenciones: si el valor sube, lo
        // retenido sube con él. Un ajuste que solo tocara el costo y el pasivo
        // dejaría la retención cuadrada contra un valor que ya no existe.
        const dRF = ret.montoRF - (parseNumber(cxp.retefuente) || 0);
        const dRICA = ret.montoRICA - (parseNumber(cxp.reteica) || 0);
        const dNeto = ret.neto - (parseNumber(cxp.neto) || (parseNumber(cxp.valor) || 0));
        const mov = (cuenta, valor) => valor === 0 ? null
          : (valor > 0 ? { cuenta, credito: valor, tercero_id: tercero ? tercero.id : null }
                       : { cuenta, debito: -valor, tercero_id: tercero ? tercero.id : null });
        try {
          const movs = [
            delta > 0 ? { cuenta: CTA_COSTO, debito: delta, tercero_id: tercero ? tercero.id : null }
                      : { cuenta: CTA_COSTO, credito: -delta, tercero_id: tercero ? tercero.id : null },
            mov(CTA_RETEFUENTE, dRF), mov(CTA_RETEICA, dRICA), mov(CTA_XPAGAR, dNeto)
          ].filter(Boolean);
          await crearAsiento(`Ajuste costo asociado ${cxp.numero} — ${cxp.concepto} — ${op.numero}`,
            { tipo: "OP", id: op.id, numero: op.numero }, movs, today());
        } catch (e) {
          alert(`⚠️ No se pudo ajustar el costo "${cxp.concepto}": ` + e.message);
          usadas.add(cxp.id);
          continue;
        }
      }
      lista = lista.map(x => x.id === cxp.id
        ? { ...x, tercero_id: tercero ? tercero.id : x.tercero_id,
            tercero_nombre: tercero ? tercero.nombre : nombre,
            tercero_cedula: x.tercero_cedula || (tercero ? (tercero.nit || "") : ""),
            concepto: c.concepto || x.concepto, valor: total,
            retefuente: ret.montoRF, retefuente_pct: ret.rfPct,
            reteica: ret.montoRICA, reteica_pct: ret.ricaPct, neto: ret.neto }
        : x);
    }
    usadas.add(c._cxpId);
  }

  // CxP pendientes de esta OP que ningún concepto usa ya: reversa + eliminar
  for (const x of lista.filter(x => x._origen && x._origen.id === op.id && x.estado === "pendiente" && !usadas.has(x.id))) {
    const valor = parseNumber(x.valor) || 0;
    if (x._asientoId && valor > 0) {
      // La reversa devuelve el asiento COMPLETO, retenciones incluidas: dejar
      // vivo un crédito en la 2365 de un costo que ya no existe le declararía a
      // la DIAN una retención que nadie practicó.
      const rf = parseNumber(x.retefuente) || 0;
      const rica = parseNumber(x.reteica) || 0;
      const neto = parseNumber(x.neto);
      try {
        const movs = [{ cuenta: CTA_XPAGAR, debito: Number.isFinite(neto) ? neto : valor, tercero_id: x.tercero_id || null }];
        if (rf > 0) movs.push({ cuenta: CTA_RETEFUENTE, debito: rf, tercero_id: x.tercero_id || null });
        if (rica > 0) movs.push({ cuenta: CTA_RETEICA, debito: rica, tercero_id: x.tercero_id || null });
        movs.push({ cuenta: CTA_COSTO, credito: valor, tercero_id: x.tercero_id || null });
        await crearAsiento(`Reversa costo asociado ${x.numero} (concepto eliminado) — ${op.numero}`,
          { tipo: "OP", id: op.id, numero: op.numero }, movs, today());
      } catch (e) { console.error("Reversa CxP:", e); }
    }
    lista = lista.filter(y => y.id !== x.id);
    conceptos.forEach(c => { if (c._cxpId === x.id) delete c._cxpId; });
  }

  saveData("cuentas_pagar", lista);
  if (bloqueadas.length) {
    alert("⚠️ Estos costos ya están en una cuenta de cobro o pagados y no se modificaron: " +
      bloqueadas.join(", ") + ".\nCualquier diferencia hay que resolverla desde Cuentas por Pagar.");
  }
}

// ─── Modal: ¿a dónde va cada costo? ─────────────────────────────────────────
// Se abre al guardar una OP con conceptos de costo sin destino confirmado. El
// sistema sugiere (memoria o heurística) y el usuario da el visto bueno.
export function renderDestinoCostosModal() {
  const d = state._destinoCostos;
  if (!d) return;
  const op = (state.data.pedidos || []).find(p => p.id === d.opId);
  if (!op) { state._destinoCostos = null; return; }
  const pendientes = (op._conceptosCosto || []).filter(c => d.ccids.includes(c._ccid));
  if (!pendientes.length) { state._destinoCostos = null; return; }

  const filas = pendientes.map(c => {
    const total = Math.round((parseNumber(c.cantidad) || 0) * (parseNumber(c.valor_tercero) || 0));
    const sel = d.sel[c._ccid];
    const sug = viaSugerida(c.concepto);
    const opciones = [
      ["ocp", "🛒 Orden de compra (OCP)"],
      ["cxp", "💸 Cuenta por pagar directa"],
      ["nada", "— No generar nada"]
    ];
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:7px 9px;">
          <div style="font-weight:700;color:${C.text};font-size:12px;">${escapeHTML(c.concepto || "Costo")}</div>
          <div style="font-size:10px;color:${C.muted};">${escapeHTML(c.proveedor_nombre || "sin tercero")} · ${fmtCOP(total)}</div>
        </td>
        <td style="padding:7px 9px;">
          <select style="${inputStyle}cursor:pointer;font-size:11.5px;"
            onchange="state._destinoCostos.sel['${c._ccid}']=this.value;renderDestinoCostosModal();">
            ${opciones.map(([v, l]) => `<option value="${v}" ${sel === v ? "selected" : ""}>${l}</option>`).join("")}
          </select>
          ${/* La segunda pregunta, solo para las órdenes de compra: es la única
                vía que causa IVA (una cuenta de cobro la firma una persona
                natural, que no lo cobra). Sin responder se calcula como
                siempre: manda la ficha del proveedor y el régimen del bien. */ ""}
          ${/* Con la cuenta de cobro se le puede mandar además el papel del
                compromiso: una orden de compra INFORMATIVA, de su propia serie
                (OCI), sin IVA y que no se ingresa ni se causa. */ ""}
          ${sel === "cxp" ? `
            <select style="${inputStyle}cursor:pointer;font-size:10.5px;padding:4px 6px;margin-top:4px;"
              onchange="state._destinoCostos.selOcp['${c._ccid}']=this.value">
              ${[["", "Sin orden de compra"], ["si", "📋 Además, OCP informativa"]]
                .map(([v, l]) => `<option value="${v}" ${(d.selOcp || {})[c._ccid] === v ? "selected" : ""}>${l}</option>`).join("")}
            </select>` : ""}
          ${sug.aprendida ? `<div style="font-size:9.5px;color:#0E7490;font-weight:700;margin-top:2px;">💡 sugerido por decisiones anteriores</div>` : ""}
        </td>
      </tr>`;
  }).join("");

  state.modal = `
    <div data-action="prevent-close" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1180;padding:11px;">
      <div style="background:white;border-radius:14px;width:600px;max-width:100%;max-height:88vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#f8fafc;">
          <div style="font-size:15px;font-weight:800;color:${C.text};">📦 ¿A dónde van los costos de ${escapeHTML(op.numero)}?</div>
          <div style="font-size:11px;color:${C.muted};margin-top:3px;line-height:1.5;">
            Un costo puede volverse <b>orden de compra</b> (mercancía o servicio que se compra formalmente) o
            <b>cuenta por pagar directa</b> (transporte y servicios de terceros que se legalizan luego con una
            cuenta de cobro; se causa al costo de la orden y afecta su margen). El sistema recuerda tu decisión
            por concepto para sugerirla la próxima vez.
          </div>
        </div>
        <div style="padding:11px 14px;">
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr>
              <th style="padding:5px 9px;text-align:left;font-size:10px;color:${C.muted};text-transform:uppercase;">Concepto</th>
              <th style="padding:5px 9px;text-align:left;font-size:10px;color:${C.muted};text-transform:uppercase;">Destino</th>
            </tr></thead>
            <tbody>${filas}</tbody>
          </table>
          <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:14px;padding-top:11px;border-top:1px solid ${C.border};">
            <button data-action="destino-costos-despues" style="${btnStyle(null, "secondary")}">Decidir después</button>
            <button data-action="destino-costos-confirmar" style="${btnStyle("#0E7490")}">✅ Confirmar destinos</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// Prepara y abre el modal para los conceptos sin destino de una OP recién
// guardada. Devuelve true si quedó algo por preguntar.
export function pedirDestinoCostos(op) {
  // Los costos SIN tercero también se preguntan. Exigir el tercero dejaba mudo
  // justo el caso normal: los costos que llegan de la cotización nacen sin él
  // (el tercero se elige después, en el buscador), así que en una CT→OP el
  // pop-up no preguntaba nada y el destino quedaba sin decidir. La decisión no
  // necesita el tercero; lo que sí lo necesita es GENERAR el documento, y eso
  // ya lo cuidan las dos sincronizaciones, que se saltan un costo sin nombre.
  const sinVia = (op._conceptosCosto || []).filter(c => {
    if (c._esComision || c._via) return false;
    const total = (parseNumber(c.cantidad) || 0) * (parseNumber(c.valor_tercero) || 0);
    return total > 0;
  });
  if (!sinVia.length) return false;
  const sel = {};
  const selOcp = {};
  sinVia.forEach(c => {
    sel[c._ccid] = viaSugerida(c.concepto).via;
    selOcp[c._ccid] = c._ocpAdemas || "";
  });
  state._destinoCostos = { opId: op.id, ccids: sinVia.map(c => c._ccid), sel, selOcp };
  renderDestinoCostosModal();
  return true;
}

// Lo retenido y el neto de una CxP. Las CxP anteriores a este desglose no
// tienen los campos: ahí el neto ES el bruto, que es lo que se les pagó.
export function retenidoDe(x) {
  return (parseNumber(x.retefuente) || 0) + (parseNumber(x.reteica) || 0);
}
export function netoDe(x) {
  const n = parseNumber(x.neto);
  return Number.isFinite(n) && x.neto != null ? n : (parseNumber(x.valor) || 0) - retenidoDe(x);
}

// ─── Pestaña: CxP directas ──────────────────────────────────────────────────
export function renderCxpDirectasTab() {
  const lista = [...(state.data.cuentas_pagar || [])].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  if (!state.cxpSel) state.cxpSel = {};
  const sel = state.cxpSel;
  const tope = topeCuentaCobro();

  const seleccionadas = lista.filter(x => sel[x.id] && x.estado === "pendiente");
  const totalSel = seleccionadas.reduce((s, x) => s + (parseNumber(x.valor) || 0), 0);
  const retenidoSel = seleccionadas.reduce((s, x) => s + retenidoDe(x), 0);
  const netoSel = seleccionadas.reduce((s, x) => s + netoDe(x), 0);
  // Una CxP sin ficha de tercero no tiene tarifa que aplicar ni identificación
  // con la que reportar la exógena: hay que completarla antes de legalizar.
  const sinFicha = seleccionadas.filter(x => !x.tercero_id);
  const terceros = [...new Set(seleccionadas.map(x => normalize(x.tercero_nombre || "")))];
  const variosTerceros = terceros.length > 1;
  // El tope es MENSUAL por persona: cuenta lo que este tercero ya legalizó en
  // cuentas de cobro este mes (una o varias), más lo que se está seleccionando.
  const mesActual = today().slice(0, 7);
  const yaEsteMes = variosTerceros || seleccionadas.length === 0 ? 0 :
    (state.data.cuentas_cobro || [])
      .filter(cc => (cc.fecha || "").slice(0, 7) === mesActual)
      .filter(cc => normalize((cc.persona || {}).nombre || "") === terceros[0])
      .reduce((s, cc) => s + (parseNumber(cc.total) || 0), 0);
  const excedeTope = yaEsteMes + totalSel > tope;
  const puedeGenerar = seleccionadas.length > 0 && !excedeTope && !variosTerceros;

  const chip = (estado) => {
    const m = {
      pendiente: ["#fef3c7", "#92400e", "Pendiente"],
      en_cuenta_cobro: ["#e0f2fe", "#075985", "En cuenta de cobro"],
      pagada: ["#d1fae5", "#065f46", "Pagada"]
    }[estado] || ["#f1f5f9", C.muted, estado];
    return `<span style="background:${m[0]};color:${m[1]};padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;white-space:nowrap;">${m[2]}</span>`;
  };

  const filas = lista.map(x => {
    const cc = x._cuentaCobroId ? (state.data.cuentas_cobro || []).find(k => k.id === x._cuentaCobroId) : null;
    return `
    <tr style="border-top:1px solid ${C.border};${sel[x.id] ? "background:#f0fdfa;" : ""}">
      <td style="padding:6px 10px;text-align:center;">
        ${x.estado === "pendiente"
          ? `<input type="checkbox" data-action="cxp-dir-toggle" data-id="${x.id}" ${sel[x.id] ? "checked" : ""} style="width:16px;height:16px;cursor:pointer;"/>`
          : ""}
      </td>
      <td style="padding:6px 10px;font-weight:700;color:${C.purple};">${escapeHTML(x.numero || "")}</td>
      <td style="padding:6px 10px;color:${C.muted};">${fmtDate(x.fecha)}</td>
      <td style="padding:6px 10px;">
        <div style="font-weight:600;color:${C.text};">${escapeHTML(x.tercero_nombre || "—")}</div>
        ${x.tercero_cedula ? `<div style="font-size:10px;color:${C.muted};">C.C. ${escapeHTML(x.tercero_cedula)}</div>` : ""}
      </td>
      <td style="padding:6px 10px;font-size:11.5px;">${escapeHTML(x.concepto || "")}</td>
      <td style="padding:6px 10px;">
        ${x._origen ? `<span data-action="ir-a-documento" data-tipo="OP" data-id="${x._origen.id}" style="color:#0F766E;font-weight:700;cursor:pointer;text-decoration:underline;font-size:11px;">${escapeHTML(x._origen.numero || "")}</span>` : "—"}
      </td>
      <td style="padding:6px 10px;">${chip(x.estado)}${cc ? `<div style="font-size:9.5px;color:${C.muted};margin-top:2px;">${escapeHTML(cc.numero)}</div>` : ""}</td>
      <td style="padding:6px 10px;font-weight:700;text-align:right;">${fmtCOP(x.valor)}</td>
      ${/* Lo retenido y el neto, que es lo que de verdad se le gira: el bruto
            solo no explica por qué el banco saca otra cifra. */ ""}
      <td style="padding:6px 10px;text-align:right;font-size:11px;">
        ${retenidoDe(x) > 0
          ? `<span style="color:#b45309;font-weight:700;">− ${fmtCOP(retenidoDe(x))}</span>
             <div style="font-size:9px;color:${C.muted};">${[
                (parseNumber(x.retefuente_pct)||0) > 0 ? `RF ${x.retefuente_pct}%` : "",
                (parseNumber(x.reteica_pct)||0) > 0 ? `ICA ${x.reteica_pct}%` : ""
             ].filter(Boolean).join(" · ")}</div>`
          : `<span style="color:${C.muted};">—</span>`}
      </td>
      <td style="padding:6px 10px;font-weight:800;text-align:right;color:#0E7490;">${fmtCOP(netoDe(x))}</td>
    </tr>`;
  }).join("");

  const barraSel = seleccionadas.length === 0 ? "" : `
    <div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;background:${excedeTope || variosTerceros ? "#fef2f2" : "#f0fdfa"};border:1px solid ${excedeTope || variosTerceros ? "#fca5a5" : "#99f6e4"};border-radius:8px;padding:9px 13px;margin-bottom:9px;">
      <div style="font-size:12px;color:${C.text};">
        <b>${seleccionadas.length}</b> seleccionada${seleccionadas.length === 1 ? "" : "s"} ·
        Bruto <b>${fmtCOP(totalSel)}</b>${retenidoSel > 0 ? ` · Retención <b style="color:#b45309;">− ${fmtCOP(retenidoSel)}</b>` : ""} ·
        Neto <b style="font-size:13.5px;color:#0E7490;">${fmtCOP(netoSel)}</b>
        <span style="color:${C.muted};font-size:10.5px;"> (tope mensual por persona: ${fmtCOP(tope)}${yaEsteMes ? ` · este mes ya lleva ${fmtCOP(yaEsteMes)}` : ""})</span>
      </div>
      ${variosTerceros ? `<div style="font-size:11px;font-weight:700;color:#b91c1c;">⚠️ Hay CxP de terceros distintos: una cuenta de cobro es de UNA sola persona.</div>` : ""}
      ${sinFicha.length ? `<div style="font-size:11px;font-weight:700;color:#b45309;">⚠️ ${sinFicha.length} sin ficha de tercero: no se les pudo calcular retención. Al generar la cuenta de cobro se piden los datos.</div>` : ""}
      ${excedeTope ? `<div style="font-size:11px;font-weight:700;color:#b91c1c;">⚠️ Supera el tope mensual de ${fmtCOP(tope)} por persona${yaEsteMes ? ` (ya lleva ${fmtCOP(yaEsteMes)} en cuentas de cobro este mes)` : ""} — quita alguna CxP.</div>` : ""}
      <div style="margin-left:auto;">
        <button data-action="cxp-dir-generar-cc" ${puedeGenerar ? "" : "disabled"}
          style="${btnStyle(puedeGenerar ? "#0E7490" : null, puedeGenerar ? null : "secondary")}${puedeGenerar ? "" : "opacity:.5;cursor:not-allowed;"}">
          🧾 Generar cuenta de cobro
        </button>
      </div>
    </div>`;

  return `
    ${barraSel}
    ${lista.length === 0 ? `
      <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};">
        <div style="font-size:34px;margin-bottom:8px;">💸</div>
        Sin cuentas por pagar directas. Nacen de los costos asociados de una OP
        (transporte, fletes, servicios) cuando eliges "Cuenta por pagar directa" como destino.
      </div>` : `
      <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead><tr style="background:#f1f5f9;">
            <th style="padding:6px 8px;width:34px;"></th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Número</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Fecha</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Tercero</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Concepto</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">OP</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Estado</th>
            <th style="padding:6px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Bruto</th>
            <th style="padding:6px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};" title="Retención en la fuente y ReteICA practicadas al causar el costo (Art. 392 E.T.)">Retención</th>
            <th style="padding:6px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Neto a pagar</th>
          </tr></thead>
          <tbody>${filas}</tbody>
        </table>
      </div>`}
  `;
}

// ─── Modal: la ficha del tercero, cuando falta ──────────────────────────────
// Se abre en el momento en que hace falta: al ir a legalizar una CxP de alguien
// que no tiene ficha. Sin ficha no hay tarifa de retención que aplicar ni
// documento con el que reportarlo en la exógena, así que preguntarlo después
// —cuando la cuenta de cobro ya está firmada— llegaría tarde.
export function renderFichaTerceroModal() {
  const d = state._fichaTercero;
  if (!d) return;
  const cxps = (state.data.cuentas_pagar || []).filter(x => d.cxpIds.includes(x.id));
  const total = cxps.reduce((s, x) => s + (parseNumber(x.valor) || 0), 0);
  const opcion = RETENCIONES_SERVICIO.find(r => r.id === d.retencion) || RETENCIONES_SERVICIO[0];
  const pct = d.pctManual != null && d.pctManual !== "" ? parseNumber(d.pctManual) : opcion.pct;
  const previa = retencionesCxpDirecta(
    { retefuente: pct, reteica: parseNumber(d.reteica) || 0, regimen_fiscal: d.regimen || "no_declarante" },
    total);

  state.modal = `
    <div data-action="prevent-close" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1190;padding:11px;">
      <div style="background:white;border-radius:14px;width:620px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#fffbeb;">
          <div style="font-size:15px;font-weight:800;color:${C.text};">👤 Faltan los datos de ${escapeHTML(d.nombre || "el tercero")}</div>
          <div style="font-size:11px;color:#92400e;margin-top:3px;line-height:1.5;">
            Para hacerle la cuenta de cobro hace falta su ficha: de ahí sale el <b>% de retención</b> que se le
            practica y el <b>documento</b> con el que se le reporta a la DIAN. Se guarda como tercero y no hay
            que volver a pedirlo.
          </div>
        </div>
        <div style="padding:12px 14px;">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Nombre completo *</label>
              <input value="${escapeHTML(d.nombre || "")}" style="${inputStyle}" oninput="state._fichaTercero.nombre=this.value"/>
            </div>
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Cédula o NIT *</label>
              <input value="${escapeHTML(d.nit || "")}" inputmode="numeric" style="${inputStyle}" oninput="state._fichaTercero.nit=this.value"/>
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Régimen fiscal</label>
              <select style="${inputStyle}cursor:pointer;" onchange="state._fichaTercero.regimen=this.value;renderFichaTerceroModal();">
                ${REGIMENES_FISCALES.map(r => `<option value="${r.value}" ${(d.regimen || "no_declarante") === r.value ? "selected" : ""}>${r.label}</option>`).join("")}
              </select>
            </div>
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Ciudad / Municipio (para el ICA)</label>
              <input value="${escapeHTML(d.ciudad || "")}" placeholder="Ej: Bogotá D.C." style="${inputStyle}" oninput="state._fichaTercero.ciudad=this.value"/>
            </div>
          </div>
          <div style="margin-bottom:10px;">
            <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">¿Qué le presta a Mathiz? — define la retención</label>
            <select style="${inputStyle}cursor:pointer;" onchange="state._fichaTercero.retencion=this.value;state._fichaTercero.pctManual=null;renderFichaTerceroModal();">
              ${RETENCIONES_SERVICIO.map(r => `<option value="${r.id}" ${d.retencion === r.id ? "selected" : ""}>${r.label}${r.pct ? ` — ${r.pct}%` : ""}</option>`).join("")}
            </select>
            ${opcion.cita ? `<div style="font-size:9.5px;color:#0E7490;font-weight:700;margin-top:3px;">⚖️ ${escapeHTML(opcion.cita)}</div>` : ""}
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">% ReteFuente (se puede ajustar)</label>
              <input type="number" step="0.01" min="0" max="100" value="${escapeHTML(String(pct))}" style="${inputStyle}text-align:right;" oninput="state._fichaTercero.pctManual=this.value;renderFichaTerceroModal();"/>
            </div>
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">% ReteICA del municipio</label>
              <input type="number" step="0.001" min="0" max="100" value="${escapeHTML(d.reteica != null ? String(d.reteica) : "0")}" style="${inputStyle}text-align:right;" oninput="state._fichaTercero.reteica=this.value;renderFichaTerceroModal();"/>
            </div>
          </div>
          <label style="display:flex;align-items:center;gap:6px;font-size:11.5px;color:${C.text};cursor:pointer;margin-bottom:10px;"
            title="Solo si la persona firma bajo juramento que no tomará costos ni deducciones. Decide si esa declaración se imprime en su cuenta de cobro.">
            <input type="checkbox" ${d.declara383 ? "checked" : ""} onchange="state._fichaTercero.declara383=this.checked;renderFichaTerceroModal();"/>
            Firma la declaración del Art. 383 E.T.
          </label>
          <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:9px 12px;margin-bottom:12px;font-size:11.5px;">
            <div style="font-size:10px;font-weight:700;color:${C.muted};text-transform:uppercase;margin-bottom:5px;">Así quedarían sus ${cxps.length} cuenta${cxps.length === 1 ? "" : "s"} por pagar</div>
            <div style="display:flex;justify-content:space-between;"><span>Bruto</span><b>${fmtCOP(total)}</b></div>
            <div style="display:flex;justify-content:space-between;color:${previa.montoRF > 0 ? "#b45309" : C.muted};">
              <span>ReteFuente ${previa.rfPct ? previa.rfPct + "%" : ""}</span>
              <b>${previa.montoRF > 0 ? "− " + fmtCOP(previa.montoRF) : "—"}</b>
            </div>
            ${previa.montoRF === 0 && previa.motivoRF ? `<div style="font-size:9.5px;color:${C.muted};">${escapeHTML(previa.motivoRF)}</div>` : ""}
            ${previa.montoRICA > 0 ? `<div style="display:flex;justify-content:space-between;color:#b45309;"><span>ReteICA ${previa.ricaPct}%</span><b>− ${fmtCOP(previa.montoRICA)}</b></div>` : ""}
            <div style="display:flex;justify-content:space-between;font-weight:800;padding-top:5px;border-top:1px solid ${C.border};margin-top:5px;color:#0E7490;">
              <span>NETO A PAGAR</span><span>${fmtCOP(previa.neto)}</span>
            </div>
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="ficha-tercero-cancelar" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="ficha-tercero-guardar" style="${btnStyle("#0E7490")}">✅ Guardar ficha y continuar</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// ─── Modal: datos de la persona para la cuenta de cobro ─────────────────────
export function renderCuentaCobroModal() {
  const d = state._ccDraft;
  if (!d) return;
  const cxps = (state.data.cuentas_pagar || []).filter(x => d.cxpIds.includes(x.id));
  const total = cxps.reduce((s, x) => s + (parseNumber(x.valor) || 0), 0);
  const tope = topeCuentaCobro();

  // Tope MENSUAL por persona: lo ya legalizado este mes + esta cuenta.
  const mes = (d.fecha || today()).slice(0, 7);
  const yaEsteMes = (state.data.cuentas_cobro || [])
    .filter(cc => (cc.fecha || "").slice(0, 7) === mes)
    .filter(cc => {
      const p = cc.persona || {};
      return (d.cedula && p.cedula && p.cedula === d.cedula) || normalize(p.nombre || "") === normalize(d.nombre || "");
    })
    .reduce((s, cc) => s + (parseNumber(cc.total) || 0), 0);
  const excedeMes = yaEsteMes + total > tope;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1180;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:560px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#f8fafc;display:flex;justify-content:space-between;align-items:center;">
          <div style="font-size:15px;font-weight:800;color:${C.text};">🧾 Nueva cuenta de cobro</div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:12px 14px;">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Nombre completo *</label>
              <input id="cc-nombre" value="${escapeHTML(d.nombre || "")}" style="${inputStyle}" oninput="state._ccDraft.nombre=this.value"/>
            </div>
            <div>
              <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Cédula *</label>
              <input id="cc-cedula" value="${escapeHTML(d.cedula || "")}" inputmode="numeric" style="${inputStyle}" oninput="state._ccDraft.cedula=this.value"/>
            </div>
          </div>
          <div style="margin-bottom:10px;">
            <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Motivo / concepto general *</label>
            <input id="cc-motivo" value="${escapeHTML(d.motivo || "")}" placeholder="Ej: Servicios de transporte de mercancía — agosto 2026" style="${inputStyle}" oninput="state._ccDraft.motivo=this.value"/>
          </div>
          <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:9px 12px;margin-bottom:10px;">
            <div style="font-size:10px;font-weight:700;color:${C.muted};text-transform:uppercase;margin-bottom:6px;">Detalle (${cxps.length} cuenta${cxps.length === 1 ? "" : "s"} por pagar)</div>
            ${cxps.map(x => `
              <div style="display:flex;justify-content:space-between;font-size:11.5px;padding:3px 0;border-bottom:1px dashed ${C.border};">
                <span>${escapeHTML(x.numero)} · ${escapeHTML(x.concepto || "")} <span style="color:${C.muted};">(${escapeHTML(x._origen?.numero || "")})</span></span>
                <b>${fmtCOP(x.valor)}</b>
              </div>`).join("")}
            ${(() => {
              // El desglose: la persona firma por el bruto y cobra el neto, y
              // eso tiene que estar a la vista ANTES de generar el documento.
              const rf = cxps.reduce((s, x) => s + (parseNumber(x.retefuente) || 0), 0);
              const rica = cxps.reduce((s, x) => s + (parseNumber(x.reteica) || 0), 0);
              if (rf + rica <= 0) return `
                <div style="display:flex;justify-content:space-between;font-size:13px;font-weight:800;padding-top:6px;color:${C.text};">
                  <span>TOTAL</span><span>${fmtCOP(total)}</span>
                </div>`;
              return `
                <div style="display:flex;justify-content:space-between;font-size:11.5px;padding-top:6px;color:${C.text};">
                  <span>Subtotal</span><span>${fmtCOP(total)}</span>
                </div>
                ${rf > 0 ? `<div style="display:flex;justify-content:space-between;font-size:11.5px;color:#b45309;"><span>ReteFuente</span><span>− ${fmtCOP(rf)}</span></div>` : ""}
                ${rica > 0 ? `<div style="display:flex;justify-content:space-between;font-size:11.5px;color:#b45309;"><span>ReteICA</span><span>− ${fmtCOP(rica)}</span></div>` : ""}
                <div style="display:flex;justify-content:space-between;font-size:13px;font-weight:800;padding-top:5px;border-top:1px solid ${C.border};margin-top:4px;color:#0E7490;">
                  <span>NETO A PAGAR</span><span>${fmtCOP(total - rf - rica)}</span>
                </div>`;
            })()}
          </div>
          ${excedeMes ? `
            <div style="background:#fef2f2;border:1px solid #fca5a5;border-radius:8px;padding:9px 12px;margin-bottom:10px;font-size:11.5px;color:#b91c1c;font-weight:700;">
              ⚠️ Con esta cuenta, ${escapeHTML(d.nombre || "la persona")} superaría el tope mensual de ${fmtCOP(tope)}:
              ya lleva ${fmtCOP(yaEsteMes)} este mes y esta cuenta suma ${fmtCOP(total)}.
            </div>` : ""}
          <div style="font-size:10px;color:${C.muted};line-height:1.5;margin-bottom:10px;">
            La cuenta de cobro sale a nombre de la persona, dirigida a
            <b>${escapeHTML(EMPRESA.nombre || "la empresa")}</b> con nuestros datos, e incluye la
            declaración del Art. 383 del Estatuto Tributario.
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="cc-generar" ${excedeMes ? "disabled" : ""}
              style="${btnStyle(excedeMes ? null : "#0E7490", excedeMes ? "secondary" : null)}${excedeMes ? "opacity:.5;cursor:not-allowed;" : ""}">
              🧾 Generar e imprimir
            </button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// ─── Pestaña: cuentas de cobro ──────────────────────────────────────────────
export function renderCuentasCobroTab() {
  const lista = [...(state.data.cuentas_cobro || [])].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  if (lista.length === 0) {
    return `
      <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};">
        <div style="font-size:34px;margin-bottom:8px;">🧾</div>
        Sin cuentas de cobro. Se generan desde la pestaña <b>CxP directas</b>, seleccionando
        las cuentas por pagar de una persona y pulsando "Generar cuenta de cobro".
      </div>`;
  }
  const filas = lista.map(cc => {
    const p = cc.persona || {};
    const esGen = cc.estado !== "pagada";
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:6px 10px;font-weight:700;color:#0E7490;">${escapeHTML(cc.numero || "")}</td>
        <td style="padding:6px 10px;color:${C.muted};">${fmtDate(cc.fecha)}</td>
        <td style="padding:6px 10px;">
          <div style="font-weight:600;color:${C.text};">${escapeHTML(p.nombre || "—")}</div>
          <div style="font-size:10px;color:${C.muted};">C.C. ${escapeHTML(p.cedula || "—")}</div>
        </td>
        <td style="padding:6px 10px;font-size:11.5px;">${escapeHTML(cc.motivo || "")}</td>
        <td style="padding:6px 10px;font-size:10.5px;color:${C.muted};">${(cc.cxpIds || []).length} CxP</td>
        <td style="padding:6px 10px;">
          ${cc.estado === "pagada"
            ? `<span style="background:#d1fae5;color:#065f46;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;">Pagada${cc._pago ? " · " + fmtDate(cc._pago.fecha) : ""}</span>`
            : `<span style="background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;">Por pagar</span>`}
        </td>
        <td style="padding:6px 10px;font-weight:800;text-align:right;">${fmtCOP(cc.total)}</td>
        <td style="padding:6px 10px;text-align:right;white-space:nowrap;">
          <button data-action="cc-pdf" data-id="${cc.id}" style="${btnStyle(null, "ghost", true)}" title="Imprimir / PDF">🖨</button>
          ${esGen ? `<button data-action="cc-pagar" data-id="${cc.id}" style="${btnStyle("#10b981", null, true)}">💳 Pagar</button>` : ""}
          ${esGen ? `<button data-action="cc-anular" data-id="${cc.id}" style="${btnStyle(null, "ghost", true)}" title="Anular (las CxP vuelven a quedar pendientes)">🗑</button>` : ""}
        </td>
      </tr>`;
  }).join("");
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f1f5f9;">
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Número</th>
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Fecha</th>
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Persona</th>
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Motivo</th>
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Detalle</th>
          <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Estado</th>
          <th style="padding:6px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Total</th>
          <th style="padding:6px 10px;"></th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
    </div>`;
}

// ─── Acciones ───────────────────────────────────────────────────────────────
import { registerActions } from "../ui/actions.js";

registerActions({
  "cxp-dir-toggle": (d) => {
    if (!state.cxpSel) state.cxpSel = {};
    state.cxpSel[d.id] = !state.cxpSel[d.id];
    render();
  },
  "cxp-dir-generar-cc": () => {
    const sel = state.cxpSel || {};
    const cxps = (state.data.cuentas_pagar || []).filter(x => sel[x.id] && x.estado === "pendiente");
    if (!cxps.length) return;
    const total = cxps.reduce((s, x) => s + (parseNumber(x.valor) || 0), 0);
    if (total > topeCuentaCobro()) {
      alert(`⚠️ La selección (${fmtCOP(total)}) supera el tope de ${fmtCOP(topeCuentaCobro())} por cuenta de cobro.`);
      return;
    }
    // Si al tercero le falta la ficha, se pide AQUÍ y no después: de la ficha
    // sale el % de retención, y una cuenta de cobro ya firmada por el bruto no
    // se puede corregir con un "ah, había que retenerle".
    const nombre = cxps[0].tercero_nombre || "";
    if (cxps.some(x => !x.tercero_id)) {
      state._fichaTercero = {
        cxpIds: cxps.map(x => x.id), nombre,
        nit: (cxps.find(x => (x.tercero_cedula || "").trim()) || {}).tercero_cedula || "",
        regimen: "no_declarante", ciudad: "", reteica: 0,
        retencion: retencionSugerida(cxps[0].concepto), pctManual: null, declara383: false
      };
      renderFichaTerceroModal();
      return;
    }
    const cedulas = [...new Set(cxps.map(x => (x.tercero_cedula || "").trim()).filter(Boolean))];
    state._ccDraft = {
      cxpIds: cxps.map(x => x.id),
      nombre,
      cedula: cedulas[0] || "",
      motivo: "",
      fecha: today()
    };
    renderCuentaCobroModal();
  },
  "ficha-tercero-cancelar": () => { state._fichaTercero = null; state.modal = null; render(); },
  "ficha-tercero-guardar": async () => {
    const d = state._fichaTercero;
    if (!d) return;
    const nombre = (d.nombre || "").trim();
    const nit = (d.nit || "").trim();
    if (!nombre || !nit) { alert("El nombre y la cédula o NIT son obligatorios: sin ellos no hay con qué reportarlo a la DIAN."); return; }
    const opcion = RETENCIONES_SERVICIO.find(r => r.id === d.retencion) || RETENCIONES_SERVICIO[0];
    const pct = (d.pctManual != null && d.pctManual !== "") ? parseNumber(d.pctManual) : opcion.pct;
    const ricaPct = parseNumber(d.reteica) || 0;

    // La ficha se crea (o se completa, si ya existía alguien con ese nombre)
    // en el mismo maestro de terceros del resto del ERP: no un registro aparte.
    const yaEsta = terceroDeCosto(nombre);
    const ficha = {
      ...(yaEsta || { id: genId() }),
      nombre, nit,
      regimen_fiscal: d.regimen || "no_declarante",
      ciudad_dian: d.ciudad || (yaEsta || {}).ciudad_dian || "",
      responsable_iva: !!(yaEsta || {}).responsable_iva,
      retefuente: pct, reteica: ricaPct, reteiva: parseNumber((yaEsta || {}).reteiva) || 0,
      declara_383: !!d.declara383 || !!opcion.declara383
    };
    saveData("proveedores", yaEsta
      ? (state.data.proveedores || []).map(p => p.id === yaEsta.id ? ficha : p)
      : [...(state.data.proveedores || []), ficha]);

    // Las CxP ya estaban causadas SIN retención. Ahora que hay tarifa, se
    // reclasifica: el costo no cambia (es el mismo servicio), lo que cambia es
    // cuánto de ese pasivo es del tercero y cuánto es de la DIAN.
    let lista = [...(state.data.cuentas_pagar || [])];
    for (const id of d.cxpIds) {
      const x = lista.find(y => y.id === id);
      if (!x || x.estado !== "pendiente") continue;
      const base = parseNumber(x.valor) || 0;
      const ret = retencionesCxpDirecta(ficha, base);
      const dRF = ret.montoRF - (parseNumber(x.retefuente) || 0);
      const dRICA = ret.montoRICA - (parseNumber(x.reteica) || 0);
      if (dRF !== 0 || dRICA !== 0) {
        try {
          const movs = [{ cuenta: CTA_XPAGAR, debito: dRF + dRICA, tercero_id: ficha.id }];
          if (dRF !== 0) movs.push({ cuenta: CTA_RETEFUENTE, credito: dRF, tercero_id: ficha.id });
          if (dRICA !== 0) movs.push({ cuenta: CTA_RETEICA, credito: dRICA, tercero_id: ficha.id });
          await crearAsiento(
            `Retención de ${x.numero} — ${nombre} (ficha completada)`,
            { tipo: "OP", id: (x._origen || {}).id || null, numero: (x._origen || {}).numero || "" },
            movs, today());
        } catch (e) {
          alert(`⚠️ No se pudo registrar la retención de ${x.numero}: ` + e.message);
          return;
        }
      }
      lista = lista.map(y => y.id === id
        ? { ...y, tercero_id: ficha.id, tercero_nombre: nombre, tercero_cedula: nit,
            retefuente: ret.montoRF, retefuente_pct: ret.rfPct,
            reteica: ret.montoRICA, reteica_pct: ret.ricaPct, neto: ret.neto }
        : y);
    }
    saveData("cuentas_pagar", lista);

    state._ccDraft = { cxpIds: d.cxpIds, nombre, cedula: nit, motivo: "", fecha: today() };
    state._fichaTercero = null;
    renderCuentaCobroModal();
  },
  "cc-generar": async () => {
    const d = state._ccDraft;
    if (!d) return;
    const nombre = (d.nombre || "").trim();
    const cedula = (d.cedula || "").trim();
    const motivo = (d.motivo || "").trim();
    if (!nombre || !cedula || !motivo) {
      alert("Faltan datos: nombre, cédula y motivo son obligatorios para la cuenta de cobro.");
      return;
    }
    const cxps = (state.data.cuentas_pagar || []).filter(x => d.cxpIds.includes(x.id) && x.estado === "pendiente");
    if (!cxps.length) { alert("Las CxP seleccionadas ya no están pendientes."); state.modal = null; render(); return; }
    const total = cxps.reduce((s, x) => s + (parseNumber(x.valor) || 0), 0);

    // La cuenta de cobro arrastra el desglose: la persona firma por el bruto y
    // cobra el neto, y el documento tiene que decir las dos cifras. Sin ellas,
    // quien recibe menos de lo que firmó no sabe por qué.
    const retefuente = cxps.reduce((s, x) => s + (parseNumber(x.retefuente) || 0), 0);
    const reteica = cxps.reduce((s, x) => s + (parseNumber(x.reteica) || 0), 0);
    const tercero = terceroDeCosto(nombre);
    const cc = {
      id: genId(), numero: await siguienteNumeroDoc("CC"), fecha: d.fecha || today(),
      persona: { nombre, cedula, tercero_id: tercero ? tercero.id : null,
                 declara_383: !!(tercero && tercero.declara_383) },
      motivo, cxpIds: cxps.map(x => x.id),
      total, retefuente, reteica, neto: total - retefuente - reteica,
      estado: "generada"
    };
    sellarAutor(cc);
    saveData("cuentas_cobro", [...(state.data.cuentas_cobro || []), cc]);
    saveData("cuentas_pagar", (state.data.cuentas_pagar || []).map(x =>
      d.cxpIds.includes(x.id) ? { ...x, estado: "en_cuenta_cobro", _cuentaCobroId: cc.id, tercero_cedula: cedula } : x));
    state._ccDraft = null;
    state.cxpSel = {};
    state.modal = null;
    render();
    exportCuentaCobroPDF(cc.id);
  },
  "cc-pdf": (d) => { exportCuentaCobroPDF(d.id); },
  "cc-pagar": async (d) => {
    const cc = (state.data.cuentas_cobro || []).find(x => x.id === d.id);
    if (!cc || cc.estado === "pagada") return;
    // Se paga el NETO: las retenciones ya salieron del pasivo al causar, así que
    // por bancos solo se mueve lo que la persona de verdad recibe.
    const cxpsCC = (state.data.cuentas_pagar || []).filter(x => (cc.cxpIds || []).includes(x.id));
    const bruto = parseNumber(cc.total) || 0;
    const retenido = Number.isFinite(parseNumber(cc.retefuente)) || Number.isFinite(parseNumber(cc.reteica))
      ? (parseNumber(cc.retefuente) || 0) + (parseNumber(cc.reteica) || 0)
      : cxpsCC.reduce((s, x) => s + (parseNumber(x.retefuente) || 0) + (parseNumber(x.reteica) || 0), 0);
    const aPagar = bruto - retenido;
    const terceroId = (cc.persona || {}).tercero_id || (cxpsCC[0] || {}).tercero_id || null;
    const medio = prompt(
      `Pagar ${cc.numero} a ${(cc.persona || {}).nombre || ""}.\n\n` +
      `Bruto ${fmtCOP(bruto)}` + (retenido > 0 ? `\nRetenciones −${fmtCOP(retenido)}` : "") +
      `\nNETO A PAGAR ${fmtCOP(aPagar)}\n\nMedio de pago (transferencia / efectivo):`, "transferencia");
    if (medio == null) return;
    const cuentaBanco = normalize(medio).includes("efect") ? "1105" : "1110";
    let asientoId = null;
    try {
      const ast = await crearAsiento(
        `Pago cuenta de cobro ${cc.numero} — ${(cc.persona || {}).nombre || ""}`,
        { tipo: "CC", id: cc.id, numero: cc.numero },
        [{ cuenta: CTA_XPAGAR, debito: aPagar, tercero_id: terceroId },
         { cuenta: cuentaBanco, credito: aPagar, tercero_id: terceroId }],
        today());
      asientoId = ast.id;
    } catch (e) {
      alert("⚠️ No se pudo registrar el pago: " + e.message);
      return;
    }
    const pago = { fecha: today(), medio: normalize(medio).includes("efect") ? "efectivo" : "transferencia" };
    saveData("cuentas_cobro", (state.data.cuentas_cobro || []).map(x =>
      x.id === cc.id ? { ...x, estado: "pagada", _pago: pago, _asientoPagoId: asientoId } : x));
    saveData("cuentas_pagar", (state.data.cuentas_pagar || []).map(x =>
      (cc.cxpIds || []).includes(x.id) ? { ...x, estado: "pagada", _pago: pago, _asientoPagoId: asientoId } : x));
    render();
    alert(`✅ ${cc.numero} pagada.\n\nAsiento: Db 2315 Costos y gastos por pagar / Cr ` +
      `${cuentaBanco === "1105" ? "1105 Caja" : "1110 Bancos"} por ${fmtCOP(aPagar)}` +
      (retenido > 0
        ? `.\n\nLas retenciones (${fmtCOP(retenido)}) ya se habían causado al registrar el costo, ` +
          `como lo exige el art. 392 E.T. (pago o abono en cuenta, el que ocurra primero), y quedan por pagar a la DIAN.`
        : "."));
  },
  "cc-anular": (d) => {
    const cc = (state.data.cuentas_cobro || []).find(x => x.id === d.id);
    if (!cc || cc.estado === "pagada") return;
    if (!confirm(`¿Anular ${cc.numero}? Las ${(cc.cxpIds || []).length} CxP vuelven a quedar pendientes (la causación del costo no se toca).`)) return;
    saveData("cuentas_pagar", (state.data.cuentas_pagar || []).map(x =>
      (cc.cxpIds || []).includes(x.id) ? { ...x, estado: "pendiente", _cuentaCobroId: null } : x));
    saveData("cuentas_cobro", (state.data.cuentas_cobro || []).filter(x => x.id !== cc.id));
    render();
  },
  "cxp-tab": (d) => { state.cxpTab = d.tab; render(); }
});
